import axios, { AxiosRequestConfig } from 'axios'
import Cookies from 'universal-cookie'
const cookies = new Cookies()

// Function để tạo axios instance với baseURL tùy chỉnh
const createAxiosInstance = (customBaseURL?: string) => {
  return axios.create({
    baseURL: customBaseURL || process.env.REACT_APP_APIS_SERVICE_URL,
    responseType: 'json',
    headers: {
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Headers': '*',
      'Access-Control-Allow-Credentials': 'true'
    },
  })
}

const axiosApp = createAxiosInstance()

axiosApp.defaults.timeout = 60000
axiosApp.defaults.headers.post['Content-Type'] = 'application/json'

axiosApp.interceptors.request.use(
  function (config) {
    // Do something before request is sent
    const token = cookies.get('access_kho_token')
    if (token) {
      config.headers.Authorization = 'Bearer ' + token
    }
    return config
  },
  function (error) {
    // Do something with request error
    return Promise.reject(error)
  }
)

axiosApp.interceptors.response.use(
  function (response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    return response.data
  },
  function (error) {
    if (error?.response?.status === 401) {
      console.log('401 - Unauthorized')
      // Handle 401 - redirect to login
      // logOut()
    } else if (error?.response?.status === 403) {
      console.log('403 - Forbidden')
      // Handle 403 - redirect to unauthorized page
      // window.location.href = '/unauthorized'
    }
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    return Promise.reject(error.response?.data || error)
  }
)

// Hàm helper để tạo request với token và baseURL tùy chỉnh
const createRequestWithToken = (token: string, customBaseURL?: string) => {
  const axiosInstance = createAxiosInstance(customBaseURL)
  
  // Apply interceptors to the custom instance
  axiosInstance.defaults.timeout = 120000
  axiosInstance.defaults.headers.post['Content-Type'] = 'application/json'
  
  axiosInstance.interceptors.request.use(
    function (config) {
      if (token) {
        config.headers.Authorization = 'Bearer ' + token
      }
      return config
    },
    function (error) {
      return Promise.reject(error)
    }
  )

  axiosInstance.interceptors.response.use(
    function (response) {
      return response.data
    },
    function (error) {
      if (error?.response?.status === 401) {
        console.log('401 - Unauthorized')
      } else if (error?.response?.status === 403) {
        console.log('403 - Forbidden')
      }
      return Promise.reject(error.response?.data || error)
    }
  )

  return {
    get: (url: string, config: AxiosRequestConfig = {}) => {
      return axiosInstance.get(url, {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${token}`
        }
      })
    },
    post: (url: string, data: any, config: AxiosRequestConfig = {}) => {
      return axiosInstance.post(url, data, {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${token}`
        }
      })
    },
    put: (url: string, data: any, config: AxiosRequestConfig = {}) => {
      return axiosInstance.put(url, data, {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${token}`
        }
      })
    },
    delete: (url: string, config: AxiosRequestConfig = {}) => {
      return axiosInstance.delete(url, {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${token}`
        }
      })
    }
  }
}

/** Base URL API backend app con để đồng bộ user từ Auth. */
function resolveSyncBaseURL(heThong: string): string {
  if (heThong === 'kho') {
    return String(process.env.REACT_APP_KHO_REDIRECT || '').replace(
      '/setting-token?redirect_url=',
      '/kho-backend/v1',
    )
  }
  if (heThong === 'muahang') {
    return String(process.env.REACT_APP_MUAHANG_REDIRECT || '').replace(
      '/setting-token?redirect_url=',
      '/muahang-backend/v1',
    )
  }
  if (heThong === 'banhang') {
    // banhang-frontend dùng `/api/v1` (vd. localhost:9998/api/v1, banhang-dev…/api/v1)
    return String(process.env.REACT_APP_BANHANG_REDIRECT || '').replace(
      '/setting-token?redirect_url=',
      '/api/v1',
    )
  }
  return ''
}

function pickAccessToken(payload: any): string {
  return (
    payload?.data?.access_token
    || payload?.access_token
    || payload?.data?.accessToken
    || payload?.accessToken
    || ''
  )
}

/** Bán hàng: 1 lần kéo toàn bộ NV từ Auth (`POST /users/dong-bo-tu-auth`). */
async function syncBanHangUsers(customBaseURL: string) {
  const token = cookies.get('access_token')
  if (!token) {
    throw new Error('Chưa đăng nhập Auth')
  }
  const api = createRequestWithToken(token, customBaseURL)
  const refreshed = await api.put('/users/sso/lam-moi-token', {})
  const accessToken = pickAccessToken(refreshed) || token
  const apiSync = createRequestWithToken(accessToken, customBaseURL)
  return apiSync.post('/users/dong-bo-tu-auth', {})
}

export function refreshToken(token: string, customBaseURL?: string) {
  const api = createRequestWithToken(token, customBaseURL)
  return api.put('/user/refresh-token', {})
}

// Export function để tạo axios instance với baseURL tùy chỉnh
export function createCustomAxiosInstance(customBaseURL: string) {
  return createAxiosInstance(customBaseURL)
}

// Export function để tạo request helper với baseURL tùy chỉnh
export function createCustomRequestWithToken(token: string, customBaseURL: string) {
  return createRequestWithToken(token, customBaseURL)
}

export async function syncUser(heThong: string, chiNhanhArray: any[]) {
  const customBaseURL = resolveSyncBaseURL(heThong)
  if (!customBaseURL) {
    throw new Error(
      heThong === 'banhang'
        ? 'Chưa cấu hình REACT_APP_BANHANG_REDIRECT'
        : `Hệ thống "${heThong}" chưa hỗ trợ đồng bộ user từ Auth`,
    )
  }

  if (heThong === 'banhang') {
    await syncBanHangUsers(customBaseURL)
    return
  }

  const token = cookies.get('access_token')
  const api = createRequestWithToken(token, customBaseURL)
  const res = await api.put('/user/refresh-token', {})
  let access_token = pickAccessToken(res)
  if (!access_token) {
    throw new Error('Không lấy được token app con')
  }
  for (const chiNhanh of chiNhanhArray) {
    try {
      const chiNhanhId = chiNhanh.id
      const duAnId = chiNhanh.duAnId
      const api2 = createRequestWithToken(access_token, customBaseURL)
      const res2 = await api2.put('/user/' + duAnId + '/' + chiNhanhId + '/doi-chinhanh', {})
      access_token = pickAccessToken(res2) || access_token
      const api3 = createRequestWithToken(access_token, customBaseURL)
      await api3.post('/user/sync/all-users', {})
    } catch (error) {
      console.error(`Error syncing chiNhanh ${chiNhanh.id}:`, error)
    }
  }
}

export async function syncUserByBranch(heThong: string, duAnId: number, chiNhanhId: number) {
  const customBaseURL = resolveSyncBaseURL(heThong)
  if (!customBaseURL) {
    // Không chặn lưu phân quyền Auth nếu app con chưa cấu hình sync.
    console.warn(`Bỏ qua syncUserByBranch: hệ thống "${heThong}" chưa cấu hình`)
    return
  }

  if (heThong === 'banhang') {
    await syncBanHangUsers(customBaseURL)
    return
  }

  const token = cookies.get('access_token')
  const api = createRequestWithToken(token, customBaseURL)
  const res = await api.put('/user/refresh-token', {})
  let access_token = pickAccessToken(res)
  if (!access_token) {
    throw new Error('Không lấy được token app con')
  }

  const api2 = createRequestWithToken(access_token, customBaseURL)
  const res2 = await api2.put('/user/' + duAnId + '/' + chiNhanhId + '/doi-chinhanh', {})
  access_token = pickAccessToken(res2) || access_token

  const api3 = createRequestWithToken(access_token, customBaseURL)
  await api3.post('/user/sync/all-users', {})
}
